const DevilrySizeMath = {
  convert(value, from, to) {
    return from === to ? value : from === 'in' ? value * 2.54 : value / 2.54;
  },
  availableRows(config, selected) {
    if (!config.complete || !selected || selected.length === 0) return [];
    const sizes = new Set(config.variants.filter((variant) => variant.available &&
      variant.options.length === selected.length && variant.options.every((value, index) =>
        index === config.sizeIndex || value === selected[index]
      )).map((variant) => variant.options[config.sizeIndex]));
    return config.profile.rows.filter((row) => sizes.has(row.size));
  },
  match(rows, width, length, unit) {
    if (!['cm', 'in'].includes(unit) || !Number.isFinite(width) || !Number.isFinite(length) || width <= 0 || length <= 0) {
      return { kind: 'invalid', rows: [] };
    }
    if (!rows.length) return { kind: 'none', rows: [] };
    const factor = unit === 'cm' ? 2.54 : 1;
    const margin = 0.05 * factor;
    const widths = rows.map((row) => row[unit][0]);
    const lengths = rows.map((row) => row[unit][1]);
    if (width < Math.min(...widths) - margin || width > Math.max(...widths) + margin ||
      length < Math.min(...lengths) - margin || length > Math.max(...lengths) + margin) {
      return { kind: 'outside', rows: [] };
    }
    const ranked = rows.map((row) => ({ ...row,
      widthDelta: row[unit][0] - width,
      lengthDelta: row[unit][1] - length,
      distance: Math.hypot(row[unit][0] - width, row[unit][1] - length) / factor,
    })).sort((a, b) => a.distance - b.distance);
    const nearWidth = ranked.filter((r) => Math.abs(r.widthDelta) <= Math.min(...ranked.map((v) => Math.abs(v.widthDelta))) + margin);
    const nearLength = ranked.filter((r) => Math.abs(r.lengthDelta) <= Math.min(...ranked.map((v) => Math.abs(v.lengthDelta))) + margin);
    if (!nearWidth.some((w) => nearLength.some((l) => l.size === w.size))) {
      return { kind: 'conflict', rows: [nearWidth[0], nearLength[0]] };
    }
    if (ranked[1] && ranked[1].distance - ranked[0].distance <= 0.05) {
      return { kind: 'ambiguous', rows: ranked.slice(0, 2) };
    }
    return { kind: 'closest', rows: ranked.slice(0, 1) };
  },
};

if (typeof module !== 'undefined') module.exports = DevilrySizeMath;

if (typeof customElements !== 'undefined' && !customElements.get('devilry-size-finder')) {
  customElements.define('devilry-size-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized || typeof HTMLDialogElement === 'undefined') return;
      this.root = this.closest('product-info');
      this.config = JSON.parse(this.querySelector('[data-df-config]').textContent);
      this.unit = this.querySelector('[data-df-units]').value;
      this.details = this.querySelector('details');
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'df-dialog';
      this.dialog.id = `FitDialog-${this.dataset.section}`;
      this.dialog.setAttribute('aria-labelledby', `DevilryFitHeading-${this.dataset.section}`);
      this.dialog.append(this.querySelector('[data-df-content]'));
      this.append(this.dialog);
      this.details.hidden = true;
      this.querySelectorAll('[data-df-enhanced], [data-df-close]').forEach((el) => { el.hidden = false; });
      this.querySelector('[data-df-imperial]').hidden = true;
      this.querySelector('[data-df-panel="chart"]').setAttribute('role', 'tabpanel');
      this.querySelector('[data-df-panel="chart"]').setAttribute('aria-labelledby', `ChartTab-${this.dataset.section}`);
      this.controls = new AbortController();
      const options = { signal: this.controls.signal };
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-df-open]');
        if (!trigger || trigger.dataset.dfOpen !== this.dataset.section) return;
        event.preventDefault();
        this.trigger = trigger;
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        this.showTab('find');
        this.updateChart();
        this.savedOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
        this.querySelector('[data-df-tab="find"]').focus();
      }, options);
      this.root.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        this.pending = true;
        this.clearResult();
      }, options);
      this.querySelector('[data-df-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.savedOverflow ?? '';
        const trigger = this.trigger?.isConnected ? this.trigger : this.root.querySelector('[data-df-open]');
        trigger?.focus();
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.onKey(event), options);
      this.querySelectorAll('[data-df-tab]').forEach((tab) => tab.addEventListener('click', () => this.showTab(tab.dataset.dfTab), options));
      this.querySelector('[data-df-units]').addEventListener('change', (event) => {
        const nextUnit = event.target.value;
        this.querySelectorAll('[data-df-width], [data-df-length]').forEach((input) => {
          if (input.value !== '' && Number.isFinite(input.valueAsNumber)) {
            input.value = Number(DevilrySizeMath.convert(input.valueAsNumber, this.unit, nextUnit).toFixed(2));
          }
        });
        this.unit = nextUnit;
        this.updateChart();
        this.clearResult();
      }, options);
      this.querySelector('[data-df-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.calculate();
      }, options);
      this.querySelectorAll('[data-df-width], [data-df-length]').forEach((input) => input.addEventListener('input', () => this.clearResult(), options));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const fresh = data.html?.querySelector('devilry-size-finder [data-df-config]');
          if (fresh) this.config = JSON.parse(fresh.textContent);
          this.pending = false;
          this.clearResult();
          this.updateChart();
        });
      }
      this.showTab('find');
      this.updateChart();
      this.initialized = true;
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.savedOverflow ?? '';
      }
      this.controls?.abort();
      this.unsubscribe?.();
      if (this.dialog) {
        this.details.append(this.dialog.querySelector('[data-df-content]'));
        this.dialog.remove();
        this.details.hidden = false;
      }
      this.initialized = false;
    }
    selectedOptions() {
      const picker = this.root.querySelector('variant-selects');
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown')).map((group) =>
        group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value
      );
    }
    showTab(name) {
      this.querySelectorAll('[data-df-tab]').forEach((tab) => {
        const active = tab.dataset.dfTab === name;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-df-panel]').forEach((panel) => { panel.hidden = panel.dataset.dfPanel !== name; });
    }
    onKey(event) {
      const tab = event.target.closest('[data-df-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const name = event.key === 'Home' ? 'find' : event.key === 'End' ? 'chart' : tab.dataset.dfTab === 'find' ? 'chart' : 'find';
        this.showTab(name);
        this.querySelector(`[data-df-tab="${name}"]`).focus();
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button, input, select, summary, [tabindex="0"]')).filter((el) => !el.disabled && el.tabIndex >= 0 && !el.closest('[hidden]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    updateChart() {
      const selected = this.selectedOptions();
      const offered = new Set(this.config.offeredSizes);
      const available = new Set(DevilrySizeMath.availableRows(this.config, selected).map((r) => r.size));
      const charts = [{ body: this.querySelector('[data-df-rows]'), unit: this.unit },
        ...Array.from(this.root.querySelectorAll('[data-dc-rows]'), (body) => ({ body, unit: body.dataset.dcRows }))];
      charts.forEach(({ body, unit }) => {
        body.replaceChildren();
        this.config.profile.rows.filter((row) => offered.has(row.size)).forEach((row) => {
          const tr = document.createElement('tr');
          const th = document.createElement('th');
          th.scope = 'row';
          th.textContent = row.size + (available.has(row.size) ? '' : ` — ${this.config.strings.unavailable}`);
          tr.append(th);
          row[unit].forEach((value) => { const td = document.createElement('td'); td.textContent = value; tr.append(td); });
          body.append(tr);
        });
      });
      const unitText = this.config.strings[this.unit === 'cm' ? 'cm' : 'inches'];
      this.querySelector('caption').textContent = `${unitText} — ${this.config.strings.garment}`;
      this.querySelectorAll('[data-df-unit-label]').forEach((el) => { el.textContent = `(${this.unit})`; });
      this.querySelectorAll('[data-df-width], [data-df-length]').forEach((input) => {
        input.placeholder = input.getAttribute(`data-df-placeholder-${this.unit}`);
      });
    }
    clearResult() {
      this.querySelector('[data-df-result]').replaceChildren();
      this.querySelectorAll('input').forEach((input) => input.removeAttribute('aria-invalid'));
    }
    calculate() {
      const width = this.querySelector('[data-df-width]');
      const length = this.querySelector('[data-df-length]');
      const rows = DevilrySizeMath.availableRows(this.config, this.selectedOptions());
      const result = this.pending ? { kind: 'pending', rows: [] } : DevilrySizeMath.match(rows, width.valueAsNumber, length.valueAsNumber, this.unit);
      const container = this.querySelector('[data-df-result]');
      container.replaceChildren();
      const heading = document.createElement('p');
      heading.className = 'df-result-heading';
      heading.textContent = this.config.strings[result.kind];
      container.append(heading);
      if (result.kind === 'invalid') {
        [width, length].forEach((input) => input.setAttribute('aria-invalid', String(!Number.isFinite(input.valueAsNumber) || input.valueAsNumber <= 0)));
        [width, length].find((input) => input.getAttribute('aria-invalid') === 'true')?.focus();
      }
      const unitText = this.unit === 'cm' ? 'cm' : 'in';
      const deltaText = (delta, positive, negative) => {
        const rounded = Number(Math.abs(delta).toFixed(1));
        return rounded === 0 ? this.config.strings.same : `${rounded} ${unitText} ${this.config.strings[delta > 0 ? positive : negative]}`;
      };
      result.rows.forEach((row) => {
        const p = document.createElement('p');
        p.className = 'df-result-option';
        const strong = document.createElement('strong');
        strong.textContent = row.size;
        const comparison = document.createElement('span');
        comparison.className = 'df-comparison';
        comparison.textContent = `${this.config.strings.difference} ${deltaText(row.widthDelta, 'wider', 'narrower')}; ${deltaText(row.lengthDelta, 'longer', 'shorter')}.`;
        p.append(strong, comparison);
        container.append(p);
      });
      if (result.rows.length) {
        const guidance = document.createElement('p');
        guidance.className = 'df-result-note';
        guidance.textContent = this.config.strings.compare_chart;
        container.append(guidance);
      }
    }
  });
}
